<template>
  <div class="flex flex-col gap-4">
    <b-label v-if="label" :label="label" class="block text-sm/6 font-medium" />

    <!-- Mode switch -->
    <div
      v-if="allowFreeText"
      class="flex items-center gap-2 rounded-lg bg-neutral-800 p-1"
      data-e2e="star-mode-switch"
    >
      <button
        type="button"
        class="flex-1 rounded-md px-4 py-1.5 text-center text-sm transition-colors"
        :class="mode === 'star' ? 'bg-white font-semibold text-black' : 'text-neutral-400'"
        data-e2e="star-mode-star"
        @click="setMode('star')"
      >
        {{ t('star_fieldset.mode_star') }}
      </button>
      <button
        type="button"
        class="flex-1 rounded-md px-4 py-1.5 text-center text-sm transition-colors"
        :class="mode === 'free' ? 'bg-white font-semibold text-black' : 'text-neutral-400'"
        data-e2e="star-mode-free"
        @click="setMode('free')"
      >
        {{ t('star_fieldset.mode_free') }}
      </button>
    </div>

    <!-- Help -->
    <div class="group relative inline-flex items-center gap-1 self-start text-sm text-gray-400">
      <Icon name="solar:info-circle-linear" size="1rem" />
      <span>{{ t('star_fieldset.help_label') }}</span>
      <div
        class="pointer-events-none absolute bottom-full left-0 z-50 mb-2 w-72 rounded bg-gray-900 px-3 py-2 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      >
        {{ t('star_fieldset.help_bubble') }}
      </div>
    </div>

    <!-- STAR mode -->
    <template v-if="mode === 'star'">
      <div class="flex flex-col gap-4">
        <div v-for="field in starFields" :key="field.key">
          <div class="mb-1 flex flex-wrap items-baseline gap-x-2">
            <b-label
              :for="`star-${field.key}`"
              :label="t(`star_fieldset.${context}.${field.key}_label`)"
              class="text-sm/6 font-medium"
            />
            <span v-if="hint" class="text-[10px] text-gray-500">{{ hint }}</span>
          </div>
          <b-textarea
            :id="`star-${field.key}`"
            v-model="field.model.value"
            :placeholder="t(`star_fieldset.${context}.${field.key}_placeholder`)"
            :rows="3"
            maxlength="1000"
            :data-e2e="`star-${field.key}`"
          />
        </div>
      </div>
    </template>

    <!-- Free-text mode -->
    <template v-else>
      <b-textarea
        v-model="freeText"
        :placeholder="t(`star_fieldset.${context}.free_placeholder`)"
        :rows="5"
        maxlength="5000"
        data-e2e="star-free"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import BTextarea from '~/components/forms/BTextarea.vue';
import BLabel from '~/components/forms/BLabel.vue';

const { t } = useI18n();

interface Props {
  modelValue?: string;
  context: 'company' | 'freelance';
  allowFreeText?: boolean;
  label?: string;
  hint?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  allowFreeText: false,
  label: '',
  hint: '',
});

const emit = defineEmits(['update:modelValue']);

const mode = ref<'star' | 'free'>('star');
const situation = ref('');
const task = ref('');
const action = ref('');
const result = ref('');
const freeText = ref('');

const starFields = [
  { key: 'situation', model: situation },
  { key: 'task', model: task },
  { key: 'action', model: action },
  { key: 'result', model: result },
];

// Tracks the last value we emitted, to tell our own round-trip updates apart
// from external ones (draft restore, edit prefill) arriving via modelValue.
let lastEmitted = '';

function composeDescription(): string {
  const blocks = [
    { header: t(`star_fieldset.${props.context}.situation_header`), value: situation.value },
    { header: t(`star_fieldset.${props.context}.task_header`), value: task.value },
    { header: t(`star_fieldset.${props.context}.action_header`), value: action.value },
    { header: t(`star_fieldset.${props.context}.result_header`), value: result.value },
  ];
  return blocks
    .filter((b) => b.value && b.value.trim())
    .map((b) => `${formatStarHeader(b.header)}\n\n${b.value.trim()}`)
    .join('\n\n');
}

function emitValue(value: string) {
  lastEmitted = value;
  emit('update:modelValue', value);
}

function applyExternalValue(value: string) {
  // A stored description cannot be split back into STAR fields, so any
  // existing/restored content is shown in free-text mode.
  if (value && value.trim()) {
    mode.value = 'free';
    freeText.value = value;
  }
}

onMounted(() => {
  applyExternalValue(props.modelValue);
});

watch(
  () => props.modelValue,
  (value) => {
    if (value === lastEmitted) return; // ignore our own round-trip
    applyExternalValue(value);
  },
);

watch([situation, task, action, result], () => {
  if (mode.value !== 'star') return;
  emitValue(composeDescription());
});

watch(freeText, () => {
  if (mode.value !== 'free') return;
  emitValue(freeText.value);
});

function setMode(target: 'star' | 'free') {
  if (target === mode.value) return;
  if (target === 'free') {
    // Prefill the free textarea from STAR blocks so nothing is lost on switch.
    freeText.value = composeDescription();
    mode.value = 'free';
    emitValue(freeText.value);
  } else {
    // No reverse parsing: STAR fields drive the description from now on.
    mode.value = 'star';
    emitValue(composeDescription());
  }
}
</script>
